/**
 * VinylAnimation — imperative DOM helpers for the helmet submit animation.
 * Uses GSAP exclusively. Extracted from the identical functions in all 6 form modals.
 */

import { gsap } from 'gsap';
import { Physics2DPlugin } from 'gsap/Physics2DPlugin';
import { VINYL_SIZE } from './constants';

gsap.registerPlugin(Physics2DPlugin);

export interface VinylAnimationRefs {
  modalContentRef: React.RefObject<HTMLDivElement | null>;
  vinylOverlayRef: React.MutableRefObject<HTMLDivElement | null>;
  spinTimelineRef: React.MutableRefObject<gsap.core.Timeline | null>;
}

export function createHelmetOverlay(): HTMLDivElement {
  const S = VINYL_SIZE;
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position: fixed; z-index: 9999; pointer-events: none; width: ${S}px; height: ${S}px; display: flex; align-items: center; justify-content: center; opacity: 0;`;

  // Pulsing glow ring behind the helmet
  const glow = document.createElement('div');
  glow.className = 'helmet-glow';
  glow.style.cssText = `position: absolute; inset: -20px; border-radius: 50%; background: radial-gradient(circle, color-mix(in srgb, var(--color-primary-800) 25%, transparent) 0%, color-mix(in srgb, var(--color-primary-800) 10%, transparent) 40%, transparent 70%); filter: blur(10px);`;
  wrapper.appendChild(glow);

  // Helmet PNG
  const helmet = document.createElement('img');
  helmet.className = 'helmet-img';
  helmet.src = '/images/helmet-vidnight.png';
  helmet.alt = 'Vidnight Helmet';
  helmet.draggable = false;
  helmet.style.cssText = `width: ${S * 0.85}px; height: ${S * 0.85}px; object-fit: contain; filter: drop-shadow(0 8px 24px rgba(30,41,59,0.3)); position: relative; z-index: 1;`;
  wrapper.appendChild(helmet);

  document.body.appendChild(wrapper);
  return wrapper;
}

export function startVinylAnimation(refs: VinylAnimationRefs, _vinylLabel: string) {
  const el = refs.modalContentRef.current;
  if (!el) return;
  if (refs.spinTimelineRef.current) refs.spinTimelineRef.current.kill();
  if (refs.vinylOverlayRef.current) {
    refs.vinylOverlayRef.current.remove();
    refs.vinylOverlayRef.current = null;
  }

  const tl = gsap.timeline();
  refs.spinTimelineRef.current = tl;
  const rect = el.getBoundingClientRect();

  gsap.set(el, {
    position: 'fixed', top: rect.top, left: rect.left,
    width: rect.width, height: rect.height, margin: 0, zIndex: 9998,
  });

  const halfSize = VINYL_SIZE / 2;
  const viewportCenterX = rect.left + rect.width / 2 - halfSize;
  const viewportCenterY = rect.top + rect.height / 2 - halfSize;

  const wrapper = createHelmetOverlay();
  refs.vinylOverlayRef.current = wrapper;
  gsap.set(wrapper, {
    left: rect.left + rect.width / 2 - halfSize,
    top: rect.top + rect.height / 2 - halfSize,
    scale: 0, opacity: 0,
  });

  const helmet = wrapper.querySelector('.helmet-img') as HTMLElement;
  const glow = wrapper.querySelector('.helmet-glow') as HTMLElement;

  // Phase 1: Modal shrinks away
  tl.to(el, {
    duration: 0.6, width: 0, height: 0,
    top: rect.top + rect.height / 2, left: rect.left + rect.width / 2,
    borderRadius: '50%', padding: 0, overflow: 'hidden', opacity: 0, ease: 'power3.in',
  });

  // Phase 2: Helmet bounces in
  tl.to(wrapper, {
    duration: 0.5, scale: 1, opacity: 1,
    left: viewportCenterX, top: viewportCenterY, ease: 'back.out(1.7)',
  }, '-=0.3');

  // Phase 3: Gentle float + wobble + glow pulse (continuous)
  const floatTween = gsap.to(helmet, {
    y: -10, duration: 1.2, repeat: -1, yoyo: true, ease: 'sine.inOut',
  });
  const wobbleTween = gsap.to(helmet, {
    rotation: 4, duration: 1.8, repeat: -1, yoyo: true, ease: 'sine.inOut',
  });
  const glowTween = gsap.to(glow, {
    scale: 1.2, opacity: 1, duration: 1.5, repeat: -1, yoyo: true, ease: 'sine.inOut',
  });

  // Phase 4: Intensity ramps up
  tl.to([floatTween, wobbleTween], { timeScale: 3, duration: 1.8, ease: 'power2.in' });
  tl.to(helmet, {
    filter: 'drop-shadow(0 0 24px color-mix(in srgb, var(--color-primary-800) 50%, transparent))',
    duration: 1.8, ease: 'power2.in',
  }, '<');
  tl.to(glow, {
    scale: 1.5, filter: 'blur(16px)', duration: 1.8, ease: 'power2.in',
  }, '<');

  // Store all continuous tweens for cleanup
  (tl as any)._rotationTween = {
    kill() { floatTween.kill(); wobbleTween.kill(); glowTween.kill(); },
  };
}

function createShockwave(x: number, y: number, success: boolean) {
  const ring = document.createElement('div');
  ring.style.cssText = `position: fixed; z-index: 9999; pointer-events: none; border-radius: 50%; width: 50px; height: 50px; border: 3px solid ${success ? '#10b981' : '#ef4444'};`;
  document.body.appendChild(ring);
  gsap.set(ring, { left: x - 25, top: y - 25, opacity: 1 });
  gsap.to(ring, { scale: 10, opacity: 0, borderWidth: 0, duration: 0.7, ease: 'power2.out', onComplete: () => ring.remove() });

  const ring2 = document.createElement('div');
  ring2.style.cssText = `position: fixed; z-index: 9999; pointer-events: none; border-radius: 50%; width: 50px; height: 50px; border: 2px solid var(--color-primary-800);`;
  document.body.appendChild(ring2);
  gsap.set(ring2, { left: x - 25, top: y - 25, opacity: 0.8 });
  gsap.to(ring2, { scale: 14, opacity: 0, borderWidth: 0, duration: 0.9, delay: 0.08, ease: 'power2.out', onComplete: () => ring2.remove() });
}

function spawnParticles(x: number, y: number, success: boolean, isMobile: boolean) {
  const count = isMobile ? 15 : 25;
  const statusColor = success ? '#10b981' : '#ef4444';
  const palette = [statusColor, statusColor, statusColor, 'var(--color-primary-800)', 'var(--color-primary-800)', 'var(--color-primary-200)', '#1e293b'];
  for (let i = 0; i < count; i++) {
    const dot = document.createElement('div');
    const size = gsap.utils.random(3, 14);
    const c = palette[Math.floor(Math.random() * palette.length)];
    const isSquare = Math.random() > 0.6;
    dot.style.cssText = `position: fixed; z-index: 9999; pointer-events: none; width: ${size}px; height: ${size}px; border-radius: ${isSquare ? '2px' : '50%'}; background: ${c};`;
    document.body.appendChild(dot);
    gsap.set(dot, { left: x, top: y, rotation: gsap.utils.random(0, 360) });
    gsap.to(dot, {
      physics2D: { velocity: gsap.utils.random(350, 900), angle: gsap.utils.random(0, 360), gravity: 650, friction: 0.04 },
      opacity: 0, rotation: `+=${gsap.utils.random(-180, 180)}`, duration: gsap.utils.random(0.8, 1.6),
      onComplete: () => dot.remove(),
    });
  }
}

function showResultIcon(cx: number, cy: number, success: boolean) {
  const size = 72;
  const icon = document.createElement('div');
  const clr = success ? '#10b981' : '#ef4444';
  icon.style.cssText = `position: fixed; z-index: 10000; pointer-events: none; width: ${size}px; height: ${size}px; left: ${cx - size / 2}px; top: ${cy - size / 2}px;`;
  icon.innerHTML = success
    ? `<svg viewBox="0 0 52 52" width="${size}" height="${size}"><circle cx="26" cy="26" r="24" fill="none" stroke="${clr}" stroke-width="2.5"/><path fill="none" stroke="${clr}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" d="M14.1 27.2l7.1 7.2 16.7-16.8"/></svg>`
    : `<svg viewBox="0 0 52 52" width="${size}" height="${size}"><circle cx="26" cy="26" r="24" fill="none" stroke="${clr}" stroke-width="2.5"/><path fill="none" stroke="${clr}" stroke-width="4" stroke-linecap="round" d="M17 17l18 18M35 17l-18 18"/></svg>`;
  document.body.appendChild(icon);
  gsap.fromTo(icon, { scale: 0, opacity: 0 }, {
    scale: 1.15, opacity: 1, duration: 0.6, delay: 0.15, ease: 'elastic.out(1, 0.5)',
    onComplete: () => {
      gsap.to(icon, { opacity: 0, scale: 0.8, duration: 0.4, delay: 0.5, onComplete: () => icon.remove() });
    },
  });
}

// --------------- Helmet-to-Bell animation helpers ---------------

function spawnElectricSparks(cx: number, cy: number, radius: number) {
  for (let i = 0; i < 14; i++) {
    const spark = document.createElement('div');
    const angle = Math.random() * Math.PI * 2;
    const dist = radius * (0.7 + Math.random() * 0.5);
    const x = cx + Math.cos(angle) * dist;
    const y = cy + Math.sin(angle) * dist;
    const len = gsap.utils.random(10, 28);
    const rot = (angle * 180) / Math.PI + gsap.utils.random(-40, 40);
    spark.style.cssText = `position:fixed;z-index:10000;pointer-events:none;width:${len}px;height:2px;background:linear-gradient(90deg,transparent,var(--color-primary-800),#fff,var(--color-primary-800),transparent);border-radius:1px;box-shadow:0 0 6px var(--color-primary-800);transform:rotate(${rot}deg);`;
    document.body.appendChild(spark);
    gsap.set(spark, { left: x, top: y, opacity: 0 });
    gsap.to(spark, {
      opacity: 1, duration: 0.04,
      delay: gsap.utils.random(0, 0.35),
      repeat: gsap.utils.random(3, 8), yoyo: true,
      onComplete: () => spark.remove(),
    });
  }
}

function spawnTrailParticles(fromX: number, fromY: number, toX: number, toY: number) {
  const count = 8;
  for (let i = 0; i < count; i++) {
    const dot = document.createElement('div');
    const size = gsap.utils.random(4, 8);
    dot.style.cssText = `position:fixed;z-index:9999;pointer-events:none;width:${size}px;height:${size}px;border-radius:50%;background:var(--color-primary-800);box-shadow:0 0 8px var(--color-primary-800);`;
    document.body.appendChild(dot);
    const t = i / count;
    const x = fromX + (toX - fromX) * t;
    const y = fromY + (toY - fromY) * t - Math.sin(t * Math.PI) * 40;
    gsap.set(dot, { left: x, top: y, opacity: 0, scale: 1 - t * 0.5 });
    gsap.to(dot, {
      opacity: 0.8, duration: 0.08,
      delay: 0.4 + i * 0.06,
      onComplete: () => {
        gsap.to(dot, { opacity: 0, scale: 0, duration: 0.25, delay: 0.1, onComplete: () => dot.remove() });
      },
    });
  }
}

function triggerBellReaction(bellEl: HTMLElement) {
  const rect = bellEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // Flash at impact
  const flash = document.createElement('div');
  flash.style.cssText = `position:fixed;z-index:10000;pointer-events:none;width:30px;height:30px;border-radius:50%;background:radial-gradient(circle,color-mix(in srgb, var(--color-primary-800) 70%, transparent),color-mix(in srgb, var(--color-primary-800) 20%, transparent) 60%,transparent);`;
  document.body.appendChild(flash);
  gsap.set(flash, { left: cx - 15, top: cy - 15 });
  gsap.to(flash, { scale: 4, opacity: 0, duration: 0.5, ease: 'power2.out', onComplete: () => flash.remove() });

  // Bell button elastic bounce
  const bellButton = bellEl.querySelector('button') || bellEl;
  gsap.fromTo(bellButton, { scale: 1.5 }, { scale: 1, duration: 0.6, ease: 'elastic.out(1, 0.3)' });
}

function screenShake() {
  const targets = document.querySelectorAll('#root header, #root aside, #root main, #root nav');
  if (targets.length > 0) {
    gsap.fromTo(targets,
      { x: -6 },
      { x: 6, duration: 0.04, repeat: 6, yoyo: true, ease: 'none',
        onComplete: () => gsap.set(targets, { clearProps: 'transform' }),
      }
    );
  }
}

// --------------- Home hero fly-to helpers ---------------

function triggerHeroLanding(heroEl: HTMLElement) {
  const rect = heroEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // Bright flash at impact point
  const flash = document.createElement('div');
  flash.style.cssText = `position:fixed;z-index:10000;pointer-events:none;width:40px;height:40px;border-radius:50%;background:radial-gradient(circle,rgba(120,200,255,0.8),rgba(120,200,255,0.3) 50%,transparent);`;
  document.body.appendChild(flash);
  gsap.set(flash, { left: cx - 20, top: cy - 20 });
  gsap.to(flash, { scale: 6, opacity: 0, duration: 0.6, ease: 'power2.out', onComplete: () => flash.remove() });

  // Hero image elastic bounce
  gsap.fromTo(heroEl, { scale: 1.12 }, { scale: 1, duration: 0.7, ease: 'elastic.out(1, 0.35)' });

  // ── Pronounced electrical dTMS pulse — single 3s burst then stop ──
  // Image: rapid brightness flashes (more intense than the click version)
  const pulseTl = gsap.timeline();
  for (let i = 0; i < 54; i++) {
    pulseTl.to(heroEl, {
      filter: 'brightness(1.6) drop-shadow(0 0 18px rgba(120,200,255,0.9))',
      duration: 0.02,
      ease: 'expo.out',
      yoyo: true,
      repeat: 1,
    }, i * 0.055);
  }
  // After the pulse train, ease back to resting state
  pulseTl.to(heroEl, {
    filter: 'brightness(1) drop-shadow(0 4px 6px rgba(0,0,0,0.15))',
    duration: 0.5,
    ease: 'power2.out',
  });

  // Floor ring: find the SVG ellipses near the hero and pulse them too
  const heroContainer = heroEl.closest('div');
  const floorSvg = heroContainer?.querySelector('svg');
  if (floorSvg) {
    const ring = floorSvg.querySelector('.floor-ring') as SVGElement | null;
    const glow = floorSvg.querySelector('.floor-glow') as SVGElement | null;
    if (ring && glow) {
      const floorTl = gsap.timeline();
      for (let i = 0; i < 54; i++) {
        floorTl.to(ring, {
          opacity: 1,
          attr: { 'stroke-width': 3.5 },
          duration: 0.02,
          ease: 'expo.out',
          yoyo: true,
          repeat: 1,
        }, i * 0.055);
        floorTl.to(glow, {
          opacity: 0.7,
          duration: 0.02,
          ease: 'expo.out',
          yoyo: true,
          repeat: 1,
        }, i * 0.055);
      }
      // Ease back to resting
      floorTl.to(ring, { opacity: 0.08, attr: { 'stroke-width': 0.8 }, duration: 0.5, ease: 'power2.out' });
      floorTl.to(glow, { opacity: 0, duration: 0.5, ease: 'power2.out' }, '<');
    }
  }
}

// --------------- Main poof trigger ---------------

export function triggerFinalPoof(refs: VinylAnimationRefs, success: boolean, isMobile: boolean) {
  const wrapper = refs.vinylOverlayRef.current;
  const target = wrapper || refs.modalContentRef.current;
  if (!target) return;
  const rect = target.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // Kill continuous animations (float, wobble, glow)
  if (refs.spinTimelineRef.current) {
    const rotTween = (refs.spinTimelineRef.current as any)._rotationTween;
    if (rotTween) rotTween.kill();
    refs.spinTimelineRef.current.kill();
    refs.spinTimelineRef.current = null;
  }

  if (refs.modalContentRef.current) gsap.set(refs.modalContentRef.current, { opacity: 0 });

  // Home page: helmet flies to the hero illustration instead of the bell
  let heroEl: HTMLElement | null = null;
  if (success && wrapper) {
    const candidate = document.querySelector<HTMLElement>('[data-home-hero]');
    if (candidate) {
      const r = candidate.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) heroEl = candidate;
    }
  }

  if (wrapper && heroEl) {
    const heroRect = heroEl.getBoundingClientRect();
    // Target: the helmet area of the illustration (upper-center of the image)
    const heroCx = heroRect.left + heroRect.width * 0.35;
    const heroCy = heroRect.top + heroRect.height * 0.2;

    // 0–400ms: Electric sparks + helmet vibration + glow flare
    spawnElectricSparks(cx, cy, VINYL_SIZE * 0.45);
    gsap.to(wrapper, { x: '+=3', duration: 0.03, repeat: 12, yoyo: true, ease: 'none' });
    const glow = wrapper.querySelector('.helmet-glow') as HTMLElement;
    if (glow) gsap.to(glow, { scale: 2.5, opacity: 1, filter: 'blur(20px)', duration: 0.4 });

    // 400–1100ms: Arc-fly to hero illustration + trail
    spawnTrailParticles(cx, cy, heroCx, heroCy);
    gsap.to(wrapper, {
      left: heroCx - VINYL_SIZE / 2, top: heroCy - VINYL_SIZE / 2,
      scale: 0.3, opacity: 0.6,
      duration: 0.7, delay: 0.4, ease: 'power3.in',
      onComplete: () => {
        gsap.set(wrapper, { opacity: 0 });
        // Landing: hero bounces + flash + checkmark
        triggerHeroLanding(heroEl!);
        screenShake();
      },
    });
    return;
  }

  // Success + bell found → helmet flies into the notification bell
  // Multiple bells may exist (desktop rail, mobile bottom nav, admin sidebar)
  // but only one is visible at a time — pick the one with a non-zero bounding rect.
  let bellEl: HTMLElement | null = null;
  if (success) {
    const candidates = document.querySelectorAll<HTMLElement>('[data-notification-bell]');
    for (const el of candidates) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) { bellEl = el; break; }
    }
  }

  if (wrapper && bellEl) {
    const bellRect = bellEl.getBoundingClientRect();
    const bellCx = bellRect.left + bellRect.width / 2;
    const bellCy = bellRect.top + bellRect.height / 2;

    // 0–400ms: Electric sparks + helmet vibration + glow flare
    spawnElectricSparks(cx, cy, VINYL_SIZE * 0.45);
    gsap.to(wrapper, { x: '+=3', duration: 0.03, repeat: 12, yoyo: true, ease: 'none' });
    const glow = wrapper.querySelector('.helmet-glow') as HTMLElement;
    if (glow) gsap.to(glow, { scale: 2.5, opacity: 1, filter: 'blur(20px)', duration: 0.4 });

    // 400–900ms: Fly to bell + trail
    spawnTrailParticles(cx, cy, bellCx, bellCy);
    gsap.to(wrapper, {
      left: bellCx - VINYL_SIZE / 2, top: bellCy - VINYL_SIZE / 2,
      scale: 0.12, opacity: 0.3,
      duration: 0.5, delay: 0.4, ease: 'power3.in',
      onComplete: () => {
        gsap.set(wrapper, { opacity: 0 });
        // 900–1200ms: Bell reaction + shake at impact
        triggerBellReaction(bellEl);
        screenShake();
      },
    });
  } else {
    // Error or no bell → classic explosion poof
    if (wrapper) gsap.set(wrapper, { opacity: 0 });
    createShockwave(cx, cy, success);
    spawnParticles(cx, cy, success, isMobile);
    showResultIcon(cx, cy, success);
    screenShake();
  }
}

// =============================================
// Batch helmet animation — polygon formations
// =============================================

export interface BatchAnimationState {
  wrappers: HTMLDivElement[];
  timeline: gsap.core.Timeline | null;
  continuousTweens: gsap.core.Tween[];
}

/** Calculate positions for N points on a regular polygon centered at (cx, cy). */
function getPolygonPositions(count: number, cx: number, cy: number, radius: number): { x: number; y: number }[] {
  if (count === 1) return [{ x: cx, y: cy }];
  if (count === 2) return [{ x: cx - radius, y: cy }, { x: cx + radius, y: cy }];
  const positions: { x: number; y: number }[] = [];
  const startAngle = -Math.PI / 2; // top
  for (let i = 0; i < count; i++) {
    const angle = startAngle + (2 * Math.PI * i) / count;
    positions.push({ x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius });
  }
  return positions;
}

/** Create a smaller helmet overlay for batch (scaled down based on count). */
function createBatchHelmetOverlay(size: number): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position:fixed;z-index:9999;pointer-events:none;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;opacity:0;`;

  const glow = document.createElement('div');
  glow.className = 'helmet-glow';
  glow.style.cssText = `position:absolute;inset:-12px;border-radius:50%;background:radial-gradient(circle,color-mix(in srgb,var(--color-primary-800) 25%,transparent) 0%,color-mix(in srgb,var(--color-primary-800) 10%,transparent) 40%,transparent 70%);filter:blur(8px);`;
  wrapper.appendChild(glow);

  const helmet = document.createElement('img');
  helmet.className = 'helmet-img';
  helmet.src = '/images/helmet-vidnight.png';
  helmet.alt = 'Vidnight Helmet';
  helmet.draggable = false;
  helmet.style.cssText = `width:${size * 0.85}px;height:${size * 0.85}px;object-fit:contain;filter:drop-shadow(0 4px 12px rgba(30,41,59,0.3));position:relative;z-index:1;`;
  wrapper.appendChild(helmet);

  document.body.appendChild(wrapper);
  return wrapper;
}

/** Calculate helmet size based on count — scales down for more helmets. */
function getBatchHelmetSize(count: number): number {
  if (count <= 2) return 120;
  if (count <= 4) return 100;
  if (count <= 6) return 85;
  if (count <= 10) return 70;
  return 60;
}

/** Calculate formation radius based on count. */
function getFormationRadius(count: number): number {
  if (count <= 2) return 50;
  if (count <= 4) return 70;
  if (count <= 6) return 90;
  if (count <= 10) return 110;
  return 120;
}

/**
 * Start the batch helmet animation. Creates N helmets in polygon formation.
 * Returns a state object to control sequential popping.
 */
export function startBatchAnimation(
  modalRef: HTMLDivElement,
  count: number,
): BatchAnimationState {
  const rect = modalRef.getBoundingClientRect();
  const tl = gsap.timeline();

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // Phase 1: Shrink the modal away
  gsap.set(modalRef, {
    position: 'fixed', top: rect.top, left: rect.left,
    width: rect.width, height: rect.height, margin: 0, zIndex: 9998,
  });
  tl.to(modalRef, {
    duration: 0.5, width: 0, height: 0,
    top: rect.top + rect.height / 2, left: rect.left + rect.width / 2,
    borderRadius: '50%', padding: 0, overflow: 'hidden', opacity: 0, ease: 'power3.in',
  });

  // Phase 2: Create helmets and animate them into formation
  const helmetSize = getBatchHelmetSize(count);
  const radius = getFormationRadius(count);
  const positions = getPolygonPositions(count, cx, cy, radius);
  const wrappers: HTMLDivElement[] = [];
  const continuousTweens: gsap.core.Tween[] = [];

  for (let i = 0; i < count; i++) {
    const wrapper = createBatchHelmetOverlay(helmetSize);
    wrappers.push(wrapper);
    const halfSize = helmetSize / 2;

    // Start from center, animate to formation position
    gsap.set(wrapper, { left: cx - halfSize, top: cy - halfSize, scale: 0, opacity: 0 });
    tl.to(wrapper, {
      duration: 0.35,
      left: positions[i].x - halfSize,
      top: positions[i].y - halfSize,
      scale: 1, opacity: 1,
      ease: 'back.out(1.4)',
    }, `-=${i === 0 ? 0.15 : 0.25}`);
  }

  // Phase 3: Add gentle float + wobble to all helmets
  for (const wrapper of wrappers) {
    const helmet = wrapper.querySelector('.helmet-img') as HTMLElement;
    const glow = wrapper.querySelector('.helmet-glow') as HTMLElement;
    if (helmet) {
      continuousTweens.push(
        gsap.to(helmet, { y: gsap.utils.random(-6, -10), duration: gsap.utils.random(1.0, 1.4), repeat: -1, yoyo: true, ease: 'sine.inOut' }),
        gsap.to(helmet, { rotation: gsap.utils.random(-3, 3), duration: gsap.utils.random(1.5, 2.0), repeat: -1, yoyo: true, ease: 'sine.inOut' }),
      );
    }
    if (glow) {
      continuousTweens.push(
        gsap.to(glow, { scale: 1.15, opacity: 0.9, duration: 1.3, repeat: -1, yoyo: true, ease: 'sine.inOut' }),
      );
    }
  }

  return { wrappers, timeline: tl, continuousTweens };
}

/**
 * Pop one helmet (by index) — same animation as single-helmet submit:
 * electric sparks + vibrate + glow flare → fly to notification bell → bell reaction + screen shake.
 * Falls back to explosion if no bell is found.
 */
export function popBatchHelmet(
  state: BatchAnimationState,
  index: number,
  isMobile: boolean,
): Promise<void> {
  return new Promise((resolve) => {
    const wrapper = state.wrappers[index];
    if (!wrapper || !wrapper.parentElement) { resolve(); return; }

    const rect = wrapper.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const helmetSize = rect.width;
    const helmet = wrapper.querySelector('.helmet-img') as HTMLElement;
    const glow = wrapper.querySelector('.helmet-glow') as HTMLElement;

    // Find the bell
    let bellEl: HTMLElement | null = null;
    const candidates = document.querySelectorAll<HTMLElement>('[data-notification-bell]');
    for (const el of candidates) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) { bellEl = el; break; }
    }

    if (bellEl) {
      const bellRect = bellEl.getBoundingClientRect();
      const bellCx = bellRect.left + bellRect.width / 2;
      const bellCy = bellRect.top + bellRect.height / 2;

      // 0–400ms: Electric sparks + helmet vibration + glow flare
      spawnElectricSparks(cx, cy, helmetSize * 0.45);
      gsap.to(wrapper, { x: '+=3', duration: 0.03, repeat: 12, yoyo: true, ease: 'none' });
      if (glow) gsap.to(glow, { scale: 2.5, opacity: 1, filter: 'blur(20px)', duration: 0.4 });

      // 400–900ms: Fly to bell + trail
      spawnTrailParticles(cx, cy, bellCx, bellCy);
      gsap.to(wrapper, {
        left: bellCx - helmetSize / 2, top: bellCy - helmetSize / 2,
        scale: 0.12, opacity: 0.3,
        duration: 0.5, delay: 0.4, ease: 'power3.in',
        onComplete: () => {
          gsap.set(wrapper, { opacity: 0 });
          wrapper.remove();
          // Bell reaction + shake at impact
          triggerBellReaction(bellEl!);
          screenShake();
          resolve();
        },
      });
    } else {
      // No bell fallback — classic explosion
      const popTl = gsap.timeline({
        onComplete: () => { wrapper.remove(); resolve(); },
      });
      if (helmet) {
        popTl.to(helmet, { x: '+=2', duration: 0.03, repeat: 8, yoyo: true, ease: 'none' }, 0);
        popTl.to(helmet, {
          filter: 'drop-shadow(0 0 16px color-mix(in srgb, var(--color-primary-800) 60%, transparent))',
          scale: 1.15, duration: 0.3, ease: 'power2.in',
        }, 0);
      }
      if (glow) {
        popTl.to(glow, { scale: 2, filter: 'blur(14px)', opacity: 1, duration: 0.3, ease: 'power2.in' }, 0);
      }
      popTl.call(() => {
        spawnParticles(cx, cy, true, isMobile);
        createShockwave(cx, cy, true);
      }, [], 0.3);
      popTl.to(wrapper, { scale: 0, opacity: 0, duration: 0.15, ease: 'power3.in' }, 0.3);
    }
  });
}

/**
 * Animate remaining helmets to reform into the N-1 polygon shape.
 */
export function reformBatchFormation(state: BatchAnimationState): Promise<void> {
  const remaining = state.wrappers.filter((w) => w.parentElement);
  if (remaining.length === 0) return Promise.resolve();

  // Find center of viewport from remaining helmets
  let sumX = 0, sumY = 0;
  for (const w of remaining) {
    const r = w.getBoundingClientRect();
    sumX += r.left + r.width / 2;
    sumY += r.top + r.height / 2;
  }
  const cx = sumX / remaining.length;
  const cy = sumY / remaining.length;

  const radius = getFormationRadius(remaining.length);
  const positions = getPolygonPositions(remaining.length, cx, cy, radius);

  return new Promise((resolve) => {
    const reformTl = gsap.timeline({ onComplete: resolve });
    for (let i = 0; i < remaining.length; i++) {
      const w = remaining[i];
      const halfSize = w.getBoundingClientRect().width / 2;
      reformTl.to(w, {
        left: positions[i].x - halfSize,
        top: positions[i].y - halfSize,
        duration: 0.35,
        ease: 'power2.inOut',
      }, 0);
    }
  });
}

/**
 * Final cleanup after all helmets have flown to bell.
 * Removes leftover DOM, shows result icon, resets modal styles.
 */
export function finishBatchAnimation(
  state: BatchAnimationState,
  modalRef: HTMLDivElement,
  success: boolean,
  _isMobile: boolean,
): Promise<void> {
  // Kill all continuous tweens
  for (const t of state.continuousTweens) t.kill();
  state.continuousTweens = [];
  if (state.timeline) { state.timeline.kill(); state.timeline = null; }

  // Remove any leftover wrappers (shouldn't be any, but safety)
  for (const w of state.wrappers) {
    if (w.parentElement) w.remove();
  }

  // Show result icon at viewport center
  const vw = window.innerWidth / 2;
  const vh = window.innerHeight / 2;
  showResultIcon(vw, vh, success);

  // Reset modal
  gsap.set(modalRef, { clearProps: 'all' });
  modalRef.style.visibility = 'hidden';
  document.body.style.overflow = '';
  document.body.style.touchAction = '';

  return new Promise((resolve) => setTimeout(resolve, 800));
}

/**
 * Full reset: kill timelines, remove overlay, clear GSAP inline styles,
 * restore body scroll.
 */
export function resetModalStyles(refs: VinylAnimationRefs) {
  if (refs.spinTimelineRef.current) {
    const rotTween = (refs.spinTimelineRef.current as any)._rotationTween;
    if (rotTween) rotTween.kill();
    refs.spinTimelineRef.current.kill();
    refs.spinTimelineRef.current = null;
  }
  if (refs.vinylOverlayRef.current) {
    refs.vinylOverlayRef.current.remove();
    refs.vinylOverlayRef.current = null;
  }
  if (refs.modalContentRef.current) {
    gsap.set(refs.modalContentRef.current, { clearProps: 'all' });
    // Keep hidden until React unmounts the modal on the next render,
    // otherwise clearProps restores visibility for a single frame.
    refs.modalContentRef.current.style.visibility = 'hidden';
  }
  document.body.style.overflow = '';
  document.body.style.touchAction = '';
}
